import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../services/axios";
import { buildWizetokLoginUrl, getWizetokBaseUrl } from "../../utils/crossAppSso";
import { checkAuthSession, pinnedAuthEmail, sessionWhoEmail } from "../../utils/authSession";

/**
 * If ReachOut already has a session, mint a handoff JWT and open Wizetok logged in.
 * ?source=wizetok → user opened Wizetok first; on miss, return to Wizetok login (do not stay on ReachOut).
 * No source → user clicked Open Wizetok from ReachOut; on miss, ask ReachOut login then continue.
 *
 * When framed (Wizetok's login page loads this silently in a hidden iframe to
 * check for a session without a visible page bounce — see AdminLogin.jsx),
 * report the result via postMessage instead of navigating: an iframe can only
 * navigate itself, not the parent tab, and a miss shouldn't be visible at all.
 */
export default function CrossAppWizetokBridge() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Connecting to Wizetok…");
  let framed = false;
  try {
    framed = window.self !== window.top;
  } catch {
    framed = true;
  }

  useEffect(() => {
    let cancelled = false;
    const source = new URLSearchParams(window.location.search).get("source") || "";

    const reportToParent = (result) => {
      if (!framed) return false;
      const targetOrigin = getWizetokBaseUrl();
      if (!targetOrigin) return false;
      window.parent.postMessage({ type: "wizetok-bridge-result", ...result }, targetOrigin);
      return true;
    };

    (async () => {
      try {
        const authed = await checkAuthSession();
        if (!authed) {
          if (cancelled) return;
          if (reportToParent({ ok: false })) return;
          if (source === "wizetok") {
            window.location.replace(`${getWizetokBaseUrl()}/admin/login?skip_bridge=1`);
            return;
          }
          setMessage("Sign in to ReachOut first, then we’ll open Wizetok.");
          navigate(`/login?return_to=${encodeURIComponent("/sso/wizetok")}`, { replace: true });
          return;
        }

        const { data } = await api.post("/auth/cross-app-handoff", { target: "wizetok" });
        const token = data?.handoffToken;
        if (!token) throw new Error("Handoff token missing");
        if (cancelled) return;
        const redirectUrl = buildWizetokLoginUrl({
          handoffToken: token,
          email: pinnedAuthEmail() || sessionWhoEmail(),
        });
        if (reportToParent({ ok: true, redirectUrl })) return;
        window.location.replace(redirectUrl);
      } catch (e) {
        console.error("[sso/wizetok] bridge failed", e);
        if (cancelled) return;
        if (reportToParent({ ok: false })) return;
        setMessage("Could not open Wizetok automatically. Opening login…");
        window.setTimeout(() => {
          window.location.assign(`${getWizetokBaseUrl()}/admin/login?skip_bridge=1`);
        }, 800);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [navigate, framed]);

  if (framed) return null;

  return (
    <div
      style={{
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f8fafc",
        color: "#334155",
        fontFamily: "system-ui, sans-serif",
        fontSize: 15,
      }}
    >
      {message}
    </div>
  );
}
